import * as React from "react";
import { FileRejection } from "react-dropzone";
import { Link, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { EDiaryConfiguration, EDiaryConfigurationFile } from "../../misc/api/apiTypes";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import DocumentTitle from "../../misc/components/DocumentTitle";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import { DragDrop } from "../../misc/DragDrop";
import { FileDownload } from "../../misc/FileDownload";
import "./FileManagerStyles.css";

export const DiaryConfigurationFiles = () => {
    const { configId } = useParams<{ configId: string }>();

    const [alert, setAlert] = React.useState<string>();
    const [config, setConfig] = React.useState<EDiaryConfiguration>();
    const [filesToUpload, setFilesToUpload] = React.useState<File[]>([]);
    const [uploading, setUploading] = React.useState<boolean>(false);
    const [complete, setComplete] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();

    const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
        setComplete(false);
        setAlert(undefined);

        if (rejected.length > 0) {
            setAlert(
                `${rejected[0].file.name} - ${rejected[0].errors.map(e => e.message).join(", ")}`
            );
            return;
        }

        setFilesToUpload([
            ...filesToUpload,
            ...accepted
        ]);
    };

    const getConfig = async () => {
        const result = await legacyClient.getEDiaryConfigurations({
            Filters: [{ Member: "Id", Values: [configId] }]
        });

        setConfig(result.Data[0]);
    };

    React.useEffect(
        () => {
            getConfig();
        },
        [configId]
    );

    if (!config) return <LoadingWithMessage message="Loading Configuration Files" />;

    const onDeleteFile = async (id: string) => {
        setComplete(false);
        await legacyClient.deleteEDiaryConfigurationFile(id);
        getConfig();
    };

    const onDownloadFile = async (file: EDiaryConfigurationFile) => {
        const responseFile = await legacyClient.downloadEDiaryConfigurationFile(file.Id);
        FileDownload(responseFile, file.Name);
    };

    const onSave = async () => {
        try {
            setComplete(false);
            setUploading(true);
            await legacyClient.uploadEDiaryConfigurationFiles(configId, filesToUpload);
            setComplete(true);

            setFilesToUpload([]);
            getConfig();
            setUploading(false);
        } catch (ex) {
            setAlert(ex.Message);
            setUploading(false);
        }

        
    }

    interface FileItemProps {
        name: string;
        created?: string;
        onDelete: () => void;
        onDownload?: () => void;
    }

    const FileItem = ({ name, created, onDelete, onDownload }: FileItemProps) => {
        return (
            <div className="col-lg-3 col-xl-2">
                <div className="panel panel-default file-box">
                    {onDelete ? <a onClick={() => onDelete()} className="file-close">
                        <i className="text-danger fa fa-times" />
                    </a> : null}
                    <div className="file-icon-box">
                        <i className="fa fa-3x fa-file" />
                    </div>
                    {onDownload ? <a onClick={() => onDownload()} className="file-download">
                        <i className="fa fa-download" />
                    </a> : null}
                    <div className="file-title">
                        <h4 className="text-primary">{name}</h4>
                         <p>
                            {created ? <small>
                                Created:{" "}
                                <LocalizedDateTime
                                    time={created}
                                    accuracy={DateTimeAccuracy.Minute}
                                />
                            </small> : <small className="text-warning">Unsaved</small>}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const currentFileItems = config.Files.map(file => (
        <FileItem key={`File_${file.Id}`} name={file.Name} onDelete={null} created={file.CreatedDateTime} onDownload={() => onDownloadFile(file)} />
    ));

    const onPendingDelete = (file: File) => {
        const remainingFiles = filesToUpload.filter(x => x.name !== file.name);
        setFilesToUpload(remainingFiles);
    }

    const pendingFileItems = filesToUpload.map(file => (
        <FileItem key={`File_${file.name}_${v4()}`} name={file.name} onDelete={() => onPendingDelete(file)} />
    ));

    return (
        <DocumentTitle title="Diary Configuration Files | Vitalograph Web Portal">
            <Alert show={alert !== undefined} category={AlertCategoryEnum.Danger}>
                {alert}
            </Alert>
            <Alert show={complete} category={AlertCategoryEnum.Success}>
                Files uploaded successfully
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Diary Configuration Files</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Link
                            className="btn btn-primary btn-outline"
                            to="/DiaryConfigurations"
                            data-automationid="DiaryConfigurationFiles_ReturnToList"
                        >
                            Return to List
                        </Link>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <DragDrop
                                id="FilesDragDrop"
                                options={{
                                    onDrop,
                                    accept: { "text/xml": [".xml"] }
                                }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        {currentFileItems}
                        {pendingFileItems}
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12 pull-right">
                    <AsyncActionButton className="btn btn-primary" isSubmitting={uploading} onClick={() => onSave()}>Save</AsyncActionButton>
                </div>
            </div>
        </DocumentTitle>
    );
};
